import React, { useState, useEffect, useMemo } from 'react';
import { getBackendService } from '../../shared/services/apiConfig';
import type { FlashPromotion, DishSnapshot } from '../../shared/types';
import CompleteReservationModal from './CompleteReservationModal';
import CustomerAvatar from '../../shared/design/components/CustomerAvatar';
import { Button, Badge, PageHeader, EmptyState, Tabs } from '../../shared/design';
import { flashFulfillment, flashOfferQuantity } from '../../shared/flashPromo';
import { parseReservationDate, reservationSection, nextSectionChange, startOfDay, endOfDay, addDays, endOfWeek, type SectionKey } from '../reservationSections';

interface ReservationsManagerProps {
    onSelectCustomer?: (id: string | number) => void;
}

interface ReservationItem {
    id: string;
    type: 'table' | 'flash';
    customerName: string;
    customerEmail: string;
    userId: string;
    date: string;
    time: string;
    guests?: number;
    itemName?: string;
    discountPrice?: number;
    originalPrice?: number;
    note?: string;
    status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
    createdAt: string;
    pointsEarned?: number;
    flashPromoId?: string;
    /** Offre flash : plat de la carte concerné, mode choisi par le client et nombre d'offres. */
    flashMenuItemId?: string;
    fulfillment?: 'takeaway' | 'dine_in';
    offerQuantity?: number;
    /** Montant (centimes, écrit par le serveur) et plats choisis par le client à la réservation. */
    amount?: number;
    dishes?: DishSnapshot[];
}

interface ToastState {
    message: string;
    kind: 'success' | 'error';
}

type TemporalFilter = 'tonight' | 'tomorrow' | 'week' | 'all';
type TypeFilter = 'all' | 'table' | 'flash';
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed';
const formatTime = (time: string): string => {
    if (!time || time === '00:00' || time === '00:00:00') return 'Horaire non précisé';
    return time.slice(0, 5);
};

const SECTION_LABELS: Record<SectionKey, string> = {
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    week: 'Cette semaine',
    past: 'Passées',
};

const euros = (cents: number) => `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
const eurosFromEuros = (v: number) => `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const ForkIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v6a3 3 0 006 0V3M11 12v9M8 3h6" />
    </svg>
);

const BoltIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
);

const ReservationsManager: React.FC<ReservationsManagerProps> = ({ onSelectCustomer }) => {
    const [reservations, setReservations] = useState<ReservationItem[]>([]);
    const [flashMap, setFlashMap] = useState<Map<string, FlashPromotion>>(new Map());
    const [temporalFilter, setTemporalFilter] = useState<TemporalFilter>('all');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [pastExpanded, setPastExpanded] = useState(false);
    const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
    const [toast, setToast] = useState<ToastState | null>(null);
    const [restaurantId, setRestaurantId] = useState<string>('');
    const [completingReservation, setCompletingReservation] = useState<ReservationItem | null>(null);
    // Heure de référence des sections ; avancée par le minuteur de bascule (plus bas).
    const [nowMs, setNowMs] = useState(() => Date.now());

    // Auto-dismiss toast
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    useEffect(() => {
        let unsubscribe: (() => void) | undefined;
        let isMounted = true;

        // Les instantanés peuvent se résoudre dans le désordre (noms clients chargés en parallèle) :
        // seul le plus récent est affiché.
        let latestSnapshot = 0;

        const load = async () => {
            try {
                const backend = await getBackendService();
                const user = backend.getCurrentUser();
                if (!user?.restaurantId || !isMounted) return;
                if (isMounted) setRestaurantId(String(user.restaurantId));

                // Fetch flash promotions in batch to enrich flash reservations with dish/price.
                let localFlashMap = new Map<string, FlashPromotion>();
                if ((backend as any).getFlashPromotions) {
                    try {
                        const promos: FlashPromotion[] = await (backend as any).getFlashPromotions(user.restaurantId);
                        localFlashMap = new Map(promos.map(p => [p.id, p]));
                        if (isMounted) setFlashMap(localFlashMap);
                    } catch (e) {
                        console.warn('Could not fetch flash promotions for enrichment', e);
                    }
                }

                const processReservations = async (raw: any[]) => {
                    const snapshot = ++latestSnapshot;
                    const transformed: ReservationItem[] = await Promise.all(raw.map(async (res: any) => {
                        let customerName = res.customerName || '';
                        let customerEmail = res.customerEmail || '';
                        if (!customerName && (backend as any).getUserById) {
                            try {
                                const customer = await (backend as any).getUserById(res.userId);
                                customerName = customer ? `${customer.firstName} ${customer.lastName}`.trim() : 'Client inconnu';
                                customerEmail = customer?.email || '';
                            } catch {
                                customerName = 'Client inconnu';
                            }
                        }
                        if (!customerName) customerName = 'Client inconnu';

                        // Infer type from flashPromoId presence, else explicit type, else default.
                        if (!res.flashPromoId && res.type && res.type !== 'flash' && res.type !== 'table') {
                            console.warn(`Reservation ${res.id} has unknown type "${res.type}", defaulting to "table"`);
                        }
                        const type: 'table' | 'flash' = res.flashPromoId ? 'flash' : (res.type === 'flash' ? 'flash' : 'table');

                        // Enrich flash reservations with dish info from the flash promotions map.
                        const flashPromo = type === 'flash' && res.flashPromoId ? localFlashMap.get(res.flashPromoId) : undefined;
                        const itemName = flashPromo?.itemName || res.itemName || res.flashPromo?.itemName;
                        const discountPrice = flashPromo?.discountPrice;
                        const originalPrice = flashPromo?.originalPrice;

                        return {
                            id: res.id,
                            type,
                            customerName,
                            customerEmail,
                            userId: res.userId,
                            date: res.date,
                            time: res.time,
                            guests: res.guests,
                            note: res.note,
                            status: res.status || 'confirmed',
                            createdAt: res.createdAt,
                            itemName,
                            discountPrice,
                            originalPrice,
                            pointsEarned: typeof res.pointsEarned === 'number' ? res.pointsEarned : undefined,
                            flashPromoId: res.flashPromoId,
                            ...(type === 'flash' ? {
                                fulfillment: flashFulfillment(res),
                                offerQuantity: flashOfferQuantity(res),
                                flashMenuItemId: flashPromo?.menuItemId,
                            } : {}),
                            ...(typeof res.amount === 'number' ? { amount: res.amount } : {}),
                            ...(Array.isArray(res.dishes) ? { dishes: res.dishes as DishSnapshot[] } : {}),
                        };
                    }));

                    if (isMounted && snapshot === latestSnapshot) {
                        setReservations(transformed);
                        setNowMs(Date.now());
                    }
                };

                // Écran quitté pendant les `await` ci-dessus : ne pas ouvrir d'abonnement orphelin.
                if (!isMounted) return;
                if ((backend as any).subscribeToRestaurantReservations && !unsubscribe) {
                    unsubscribe = (backend as any).subscribeToRestaurantReservations(user.restaurantId, processReservations);
                } else {
                    const all = await (backend as any).getRestaurantReservations?.(user.restaurantId)
                        || await (backend as any).getReservations?.(user.restaurantId)
                        || [];
                    await processReservations(all);
                    if (isMounted && backend.subscribe && !unsubscribe) {
                        unsubscribe = backend.subscribe(load);
                    }
                }
                if (!isMounted) { unsubscribe?.(); unsubscribe = undefined; }
            } catch (error) {
                console.error("Erreur de chargement des réservations", error);
            }
        };

        load();
        return () => {
            isMounted = false;
            if (unsubscribe) unsubscribe();
        };
    }, []);

    const trackUpdating = (id: string, fn: () => Promise<void>) => {
        setUpdatingIds(prev => { const n = new Set(prev); n.add(id); return n; });
        return fn().finally(() => {
            setUpdatingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
        });
    };

    // Le bouton "Terminée" sur une résa table ouvre le modal de complétion en 2 étapes
    // (data → method). Voir CompleteReservationModal pour les 3 chemins (classic / qr / nfc).
    const openCompleteTableModal = (r: ReservationItem) => {
        if (!restaurantId) {
            console.warn(`Cannot open complete modal for ${r.id}: restaurantId not yet loaded`);
            setToast({ message: 'Chargement en cours, réessayez dans un instant', kind: 'error' });
            return;
        }
        setCompletingReservation(r);
    };

    // Retrait d'une offre à emporter : le serveur crédite le prix de l'offre, comme une clôture
    // de table, sans double crédit avec le tap (mode démo : simple changement de statut).
    const handleCompleteFlash = async (r: ReservationItem) => {
        await trackUpdating(r.id, async () => {
            try {
                const backend = await getBackendService();
                if ((backend as any).completeTableReservationClassic) {
                    const res = await (backend as any).completeTableReservationClassic({ reservationId: r.id });
                    setToast({
                        message: res.alreadyCredited
                            ? `Retrait confirmé pour ${r.customerName} (déjà crédité aujourd'hui)`
                            : `Retrait confirmé pour ${r.customerName}${res.pointsEarned ? ` · ${res.pointsEarned} points crédités` : ''}`,
                        kind: 'success',
                    });
                    return;
                }
                if (!(backend as any).updateReservationStatus) {
                    throw new Error('Mise à jour non supportée par ce backend');
                }
                await (backend as any).updateReservationStatus(r.id, 'completed');
                setToast({ message: `Retrait confirmé pour ${r.customerName}`, kind: 'success' });
            } catch (error: any) {
                console.error('Complete flash reservation failed:', error);
                setToast({ message: 'Mise à jour impossible. Veuillez réessayer.', kind: 'error' });
            }
        });
    };

    const handleCancel = async (r: ReservationItem) => {
        await trackUpdating(r.id, async () => {
            try {
                const backend = await getBackendService();
                if (!(backend as any).updateReservationStatus) {
                    throw new Error('Mise à jour non supportée par ce backend');
                }
                await (backend as any).updateReservationStatus(r.id, 'cancelled');
            } catch (error: any) {
                console.error('Cancel reservation failed:', error);
                setToast({ message: 'Annulation impossible. Veuillez réessayer.', kind: 'error' });
            }
        });
    };

    // Demande de réservation envoyée par le client depuis l'app : le restaurateur l'accepte
    // (elle compte alors pour le tap NFC) ou la refuse.
    const handleAccept = async (r: ReservationItem) => {
        await trackUpdating(r.id, async () => {
            try {
                const backend = await getBackendService();
                if (!(backend as any).updateReservationStatus) {
                    throw new Error('Mise à jour non supportée par ce backend');
                }
                await (backend as any).updateReservationStatus(r.id, 'confirmed');
                setToast({ message: `Réservation acceptée pour ${r.customerName}`, kind: 'success' });
            } catch (error: any) {
                console.error('Accept reservation failed:', error);
                setToast({ message: 'Acceptation impossible. Veuillez réessayer.', kind: 'error' });
            }
        });
    };

    // Sections calculées par rapport à `nowMs`, que le minuteur de bascule avance au moment exact
    // où une réservation change de section (heure atteinte, fin de service, minuit).
    const now = new Date(nowMs);
    const tomorrowStart = startOfDay(addDays(now, 1));
    const tomorrowEnd = endOfDay(addDays(now, 1));
    const weekEnd = endOfWeek(now);

    const decoratedReservations = useMemo(() => {
        return reservations.map(r => {
            const when = parseReservationDate(r.date, r.time);
            if (!when || isNaN(when.getTime())) {
                console.warn(`Reservation ${r.id} has invalid date/time: ${r.date} ${r.time}`);
                return { ...r, when: null as Date | null, section: 'past' as SectionKey };
            }
            return { ...r, when, section: reservationSection({ type: r.type, status: r.status, when, fulfillment: r.fulfillment }, now) };
        });
    }, [reservations, nowMs]);

    // Minuteur de bascule : réveille l'écran au prochain changement de section (une réservation
    // atteint son heure ou sa fin de service, ou minuit passe), au plus tard dans 15 min.
    useEffect(() => {
        const next = nextSectionChange(decoratedReservations.map(r => r.when), nowMs);
        const timer = setTimeout(() => setNowMs(Date.now()), Math.max(1_000, next - Date.now() + 500));
        return () => clearTimeout(timer);
    }, [decoratedReservations, nowMs]);

    const filteredReservations = useMemo(() => {
        return decoratedReservations.filter(r => {
            if (typeFilter !== 'all' && r.type !== typeFilter) return false;
            if (statusFilter !== 'all' && r.status !== statusFilter) return false;
            // « Aujourd'hui » = exactement la section du même nom (tables en service comprises).
            if (temporalFilter === 'tonight') {
                if (r.section !== 'today') return false;
            } else if (temporalFilter === 'tomorrow') {
                if (!r.when || r.when < tomorrowStart || r.when > tomorrowEnd) return false;
            } else if (temporalFilter === 'week') {
                if (!r.when || r.when < now || r.when > weekEnd) return false;
            }
            return true;
        });
    }, [decoratedReservations, typeFilter, statusFilter, temporalFilter, nowMs]);

    const grouped = useMemo(() => {
        const g: Record<SectionKey, typeof filteredReservations> = { today: [], tomorrow: [], week: [], past: [] };
        for (const r of filteredReservations) {
            g[r.section].push(r);
        }
        (['today', 'tomorrow', 'week'] as SectionKey[]).forEach(k => {
            g[k].sort((a, b) => (a.when?.getTime() ?? 0) - (b.when?.getTime() ?? 0));
        });
        g.past.sort((a, b) => (b.when?.getTime() ?? 0) - (a.when?.getTime() ?? 0));
        return g;
    }, [filteredReservations]);

    const counts = useMemo(() => {
        const upcoming = filteredReservations.filter(r => r.status === 'confirmed' && r.section !== 'past').length;
        const pendingPast = filteredReservations.filter(r => r.status === 'confirmed' && r.section === 'past').length;
        const completed = filteredReservations.filter(r => r.status === 'completed').length;
        const cancelled = filteredReservations.filter(r => r.status === 'cancelled').length;
        const toConfirm = filteredReservations.filter(r => r.status === 'pending').length;
        return { upcoming, pendingPast, completed, cancelled, toConfirm };
    }, [filteredReservations]);

    const visibleUpcomingSections: SectionKey[] =
        temporalFilter === 'tonight' ? ['today']
            : temporalFilter === 'tomorrow' ? ['tomorrow']
                : ['today', 'tomorrow', 'week']; // 'week' and 'all' share the same section set

    const showPastSection = (temporalFilter === 'all') && grouped.past.length > 0;

    /* ── Carte d'une réservation sur la timeline ─────────────────────────── */
    const renderCard = (r: typeof filteredReservations[number], isLast: boolean) => {
        const isCancelled = r.status === 'cancelled';
        const isCompleted = r.status === 'completed';
        const isPending = r.status === 'pending';
        const isConfirmed = r.status === 'confirmed';
        const isPastConfirmed = isConfirmed && r.section === 'past';
        const isUpdating = updatingIds.has(r.id);
        const isFlash = r.type === 'flash';
        const isDineIn = isFlash && r.fulfillment === 'dine_in';
        const isTakeaway = isFlash && !isDineIn;
        const covers = typeof r.guests === 'number' && r.guests > 0
            ? <span className="text-sm text-gray-500">{r.guests} couvert{r.guests > 1 ? 's' : ''}</span>
            : null;
        const offers = r.offerQuantity ?? 1;
        const dishName = r.itemName && r.itemName !== 'Vente Flash' ? r.itemName : null;
        const displayTime = formatTime(r.time);
        const noTime = displayTime === 'Horaire non précisé';
        const muted = isCancelled || isCompleted;
        const dishesTotal = r.dishes?.reduce((s, d) => s + d.quantity * d.unitPrice, 0) ?? 0;

        return (
            <li key={r.id} className="relative flex gap-4 sm:gap-6">
                {/* Heure sur le fil */}
                <div className="relative flex w-16 flex-shrink-0 flex-col items-center sm:w-20">
                    <span className={`z-10 inline-flex h-12 min-w-[3.5rem] items-center justify-center rounded-full px-2 font-display text-sm sm:h-14 sm:min-w-[4rem] sm:text-base ${
                        isPending ? 'bg-warning text-cream' : muted ? 'bg-cream-200 text-gray-500' : isPastConfirmed ? 'bg-brand-700 text-cream' : 'bg-brand-500 text-cream'
                    }`}>
                        {noTime ? '—' : displayTime}
                    </span>
                    {!isLast && <span className="absolute top-12 bottom-[-1rem] w-px bg-brand-200 sm:top-14" aria-hidden="true" />}
                </div>

                {/* Carte */}
                <div className={`mb-4 min-w-0 flex-1 rounded-3xl border bg-white p-4 shadow-sm transition-opacity sm:p-5 ${
                    isPending ? 'border-warning/40' : 'border-black/[0.06]'} ${muted ? 'opacity-60' : ''} ${isUpdating ? 'pointer-events-none opacity-50' : ''}`}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 items-start gap-3">
                            <CustomerAvatar
                                name={r.customerName || '?'}
                                size="md"
                                verified={Boolean(r.userId)}
                                onClick={onSelectCustomer && r.userId ? () => onSelectCustomer(r.userId) : undefined}
                            />
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    {onSelectCustomer && r.userId ? (
                                        <button type="button" onClick={() => onSelectCustomer(r.userId)} className="font-semibold text-gray-900 hover:text-brand-600">{r.customerName}</button>
                                    ) : (
                                        <span className="font-semibold text-gray-900">{r.customerName}</span>
                                    )}
                                    {isFlash && <Badge variant="secondary" size="sm">Offre flash</Badge>}
                                    {isFlash && <Badge variant={isDineIn ? 'info' : 'neutral'} size="sm">{isDineIn ? 'Sur place' : 'À emporter'}</Badge>}
                                    {!isTakeaway && covers}
                                    {isDineIn && <span className="text-sm text-gray-500">{offers} offre{offers > 1 ? 's' : ''}</span>}
                                    {isPending && <Badge variant="warning" size="sm" dot>À confirmer</Badge>}
                                    {isPastConfirmed && <Badge variant="error" size="sm">À clôturer</Badge>}
                                    {isCancelled && <Badge variant="neutral" size="sm">Annulée</Badge>}
                                    {isCompleted && (
                                        <Badge variant="success" size="sm">{isTakeaway ? 'Retirée' : 'Terminée'}{typeof r.pointsEarned === 'number' && r.pointsEarned > 0 ? ` · +${r.pointsEarned} pts` : ''}</Badge>
                                    )}
                                </div>
                                {(() => {
                                    const parts: React.ReactNode[] = [];
                                    if (noTime) parts.push('Horaire non précisé');
                                    if ((r.section === 'past' || r.section === 'week') && r.when) parts.push(r.when.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }));
                                    if (isFlash && dishName) parts.push(<><span className="font-medium text-gray-700">{dishName}</span>{typeof r.discountPrice === 'number' && <> à <b className="text-brand-600">{eurosFromEuros(r.discountPrice)}</b>{typeof r.originalPrice === 'number' && r.originalPrice > r.discountPrice && <s className="ml-1 text-gray-400">{eurosFromEuros(r.originalPrice)}</s>}</>}</>);
                                    if (isFlash && !dishName) parts.push('Offre flash');
                                    return parts.length ? (
                                        <p className="mt-0.5 text-sm text-gray-500">
                                            {parts.map((part, i) => <React.Fragment key={i}>{i > 0 && ' · '}{part}</React.Fragment>)}
                                        </p>
                                    ) : null;
                                })()}
                                {r.dishes && r.dishes.length > 0 && (
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        {r.dishes.map((d, i) => (
                                            <span key={i} className="inline-flex h-6 items-center rounded-full bg-cream-200 px-2.5 text-xs font-medium text-gray-800">{d.quantity > 1 ? `${d.quantity} × ` : ''}{d.name}</span>
                                        ))}
                                        <span className="text-xs text-gray-500">· {euros(dishesTotal)} choisis à la réservation</span>
                                    </div>
                                )}
                                {r.note && <p className="mt-1.5 text-sm italic text-gray-500">« {r.note} »</p>}
                            </div>
                        </div>

                        {(isPending || isConfirmed) && (
                            <div className="flex flex-shrink-0 flex-wrap gap-2 sm:justify-end">
                                {isPending && (
                                    <>
                                        <Button size="sm" variant="primary" onClick={() => handleAccept(r)} disabled={isUpdating}>Accepter</Button>
                                        <Button size="sm" variant="outline" onClick={() => handleCancel(r)} disabled={isUpdating}>Refuser</Button>
                                    </>
                                )}
                                {isConfirmed && (
                                    <>
                                        {isTakeaway
                                            ? <Button size="sm" variant="secondary" onClick={() => handleCompleteFlash(r)} disabled={isUpdating}>Retrait effectué</Button>
                                            : <Button size="sm" variant="primary" onClick={() => openCompleteTableModal(r)} disabled={isUpdating}>Clôturer la table</Button>}
                                        <Button size="sm" variant="ghost" onClick={() => handleCancel(r)} disabled={isUpdating}>Annuler</Button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </li>
        );
    };

    const renderSection = (key: SectionKey, items: typeof filteredReservations) => {
        if (items.length === 0) return null;
        return (
            <section key={key} aria-label={SECTION_LABELS[key]}>
                <div className="mb-3 flex items-baseline gap-2">
                    <h2 className="section-title">{SECTION_LABELS[key]}</h2>
                    <span className="text-sm text-gray-500">{items.length} réservation{items.length > 1 ? 's' : ''}</span>
                </div>
                <ul>{items.map((r, i) => renderCard(r, i === items.length - 1))}</ul>
            </section>
        );
    };

    const temporalFilters: { id: TemporalFilter; label: string }[] = [
        { id: 'tonight', label: "Aujourd'hui" },
        { id: 'tomorrow', label: 'Demain' },
        { id: 'week', label: 'Cette semaine' },
        { id: 'all', label: 'Tout' },
    ];
    const chip = (active: boolean) =>
        `inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium leading-none transition-colors ${active ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`;

    const hasAnyVisible = visibleUpcomingSections.some(k => grouped[k].length > 0) || (showPastSection && grouped.past.length > 0);

    return (
        <div className="space-y-6 sm:space-y-8">
            <PageHeader
                title="Réservations"
                subtitle={
                    <>
                        <b className="text-gray-900">{counts.upcoming}</b> à venir
                        {counts.toConfirm > 0 && <> · <b className="text-warning">{counts.toConfirm}</b> à confirmer</>}
                        {counts.pendingPast > 0 && <> · <b className="text-brand-600">{counts.pendingPast}</b> à clôturer</>}
                        {' '}· {counts.completed} terminée{counts.completed > 1 ? 's' : ''} · {counts.cancelled} annulée{counts.cancelled > 1 ? 's' : ''}
                    </>
                }
            />

            {/* Filtres — même vocabulaire que le site : une liste, filtrée comme vous voulez */}
            <div className="flex flex-wrap items-center gap-2">
                <Tabs
                    tabs={temporalFilters.map(f => ({ id: f.id, label: f.label }))}
                    activeTab={temporalFilter}
                    onChange={(id) => setTemporalFilter(id as TemporalFilter)}
                    variant="filter"
                    ariaLabel="Période"
                />
                <span className="mx-1 hidden h-6 w-px bg-black/10 sm:inline-block" aria-hidden="true" />
                <button type="button" onClick={() => setTypeFilter(typeFilter === 'table' ? 'all' : 'table')} className={chip(typeFilter === 'table')} aria-pressed={typeFilter === 'table'}><ForkIcon className="h-4 w-4" />Tables</button>
                <button type="button" onClick={() => setTypeFilter(typeFilter === 'flash' ? 'all' : 'flash')} className={chip(typeFilter === 'flash')} aria-pressed={typeFilter === 'flash'}><BoltIcon className="h-4 w-4" />Offres flash</button>
                <span className="mx-1 hidden h-6 w-px bg-black/10 sm:inline-block" aria-hidden="true" />
                {([['pending', counts.toConfirm > 0 ? `À confirmer (${counts.toConfirm})` : 'À confirmer'], ['confirmed', 'Confirmées'], ['completed', 'Terminées'], ['cancelled', 'Annulées']] as [StatusFilter, string][]).map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setStatusFilter(statusFilter === id ? 'all' : id)} className={chip(statusFilter === id)} aria-pressed={statusFilter === id}>{label}</button>
                ))}
            </div>

            {!hasAnyVisible ? (
                <div className="rounded-3xl border border-black/[0.06] bg-white shadow-sm">
                    <EmptyState
                        icon={<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 3v3m8-3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" /></svg>}
                        title="Aucune réservation"
                        description={temporalFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all'
                            ? 'Rien ne correspond à ces filtres.'
                            : "Vos clients réservent depuis l'app : les demandes apparaissent ici en temps réel, et vous les acceptez d'un geste."}
                        secondaryAction={temporalFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all'
                            ? { label: 'Réinitialiser les filtres', onClick: () => { setTemporalFilter('all'); setTypeFilter('all'); setStatusFilter('all'); } }
                            : undefined}
                    />
                </div>
            ) : (
                <div className="space-y-8">
                    {visibleUpcomingSections.map(k => renderSection(k, grouped[k]))}

                    {showPastSection && (
                        <section aria-labelledby="past-section-heading">
                            <button
                                type="button"
                                onClick={() => setPastExpanded(v => !v)}
                                aria-expanded={pastExpanded}
                                aria-controls="past-section-list"
                                className="mb-3 flex w-full items-baseline gap-2 text-left"
                            >
                                <span id="past-section-heading" className="section-title text-gray-500">{SECTION_LABELS.past}</span>
                                <span className="text-sm text-gray-500">{grouped.past.length} réservation{grouped.past.length > 1 ? 's' : ''}</span>
                                <svg className={`ml-auto h-5 w-5 text-gray-400 transition-transform ${pastExpanded ? 'rotate-180' : ''}`} aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {pastExpanded && <ul id="past-section-list">{grouped.past.map((r, i) => renderCard(r, i === grouped.past.length - 1))}</ul>}
                        </section>
                    )}
                </div>
            )}

            {toast && (
                <div role="status" aria-live="polite" className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-2xl px-4 py-3 text-sm font-medium shadow-xl ${toast.kind === 'success' ? 'bg-gray-900 text-cream' : 'bg-brand-600 text-cream'}`}>
                    {toast.message}
                </div>
            )}

            {completingReservation && (
                <CompleteReservationModal
                    reservation={{
                        id: completingReservation.id,
                        customerName: completingReservation.customerName,
                        userId: completingReservation.userId,
                        amount: completingReservation.amount,
                        dishes: completingReservation.dishes,
                        flashPromoId: completingReservation.flashPromoId,
                        flashMenuItemId: completingReservation.flashMenuItemId,
                        offerQuantity: completingReservation.offerQuantity,
                    }}
                    restaurantId={restaurantId}
                    onClose={() => setCompletingReservation(null)}
                    onCompleted={(result) => {
                        const name = completingReservation.customerName;
                        let message: string;
                        if (result.alreadyCredited) {
                            message = `Réservation terminée pour ${name} (déjà créditée précédemment)`;
                        } else if (result.creditApplied && result.pointsEarned) {
                            const tierMsg = result.newTierName ? ` · niveau ${result.newTierName} débloqué !` : '';
                            message = `Terminée · ${result.pointsEarned} points crédités à ${name}${tierMsg}`;
                        } else if (result.pointsEarned) {
                            message = `Réservation terminée · ${result.pointsEarned} points crédités à ${name}`;
                        } else {
                            message = `Réservation terminée pour ${name}`;
                        }
                        setToast({ message, kind: 'success' });
                        setCompletingReservation(null);
                    }}
                />
            )}
        </div>
    );
};

export default ReservationsManager;
